import numeral from 'numeral';
import moment from 'moment';
import { getCoaString } from 'data/categories/retrievers';
import { getAccountString, getCurrencyString } from 'data/accounts/retrievers';
import { getTagById } from 'data/tags/retrievers';
import { formatNumber } from 'components/QuickenControls/AmountField';
import { isSplitTxn } from 'data/transactions/utils';


/**
 * SMART SEARCH tricks
 *
 *
 * If the string typed in evaluates to a number (Not NaN), then we treat it as a number.
 *
 * If the user starts expression with a '-' or a '+' it restricts the search to positive or negative values only
 * If the user types an & sign before the number, we look for an amount in the range of the 2 following comma separated values
 * If the user types a >#, we look for all income and expenses whose absolute value is greater than the amount given
 * If the user types a <#, we look for all income and expenses whose absolute value is less than the amount given
 * If the user types a ~ sign before the expression, or adds 'ish' to the end of it, we look for values relatively close to the value
 *
 * If the filter typed in is a valid DATE, then DATE SEARCH is used
 * >date is all dates after this date
 * <date is all dates before this date
 * ~ at the beginning, or 'ish' added to the end will search 15 days on either side of the date
 * &date,date will search for dates between the 2 given dates
 *
 * For TEXT searches, we remove all punctuation characters (from both the search string, and the string bases search targets
 * UNLESS the user types a '=' as the first character, then an exact string match is considered, including case
 * ALL text searching is case insensitive
 *
 * You may combine expressions using the boolean 'AND' operator expressed as .and.
 *   "+>02/23/2020 .and. Amazon"
 *
 * If the filter expression (can be between .AND. and .OR.) is 'reviewed' or 'notreviewed' then it will filter based on reviewed status
 *
 */
export function filterTransactionsByExpression(txns, _filter) {

  if (_filter && _filter.length > 0) {

    const expressions = _filter.split('.AND.');

    // if multiple expressions, successively filter them
    if (expressions.length > 1) {
      let results = txns;
      expressions.forEach((phrase) => {
        results = filterTransactionsByExpression(results, phrase.trim());
      });
      return results;
    }

    let filter = _filter;
    let fuzzy = false;

    // Is this a fuzzy search?
    if (filter.slice(-3) === 'ish') {
      filter = filter.slice(0, -3);
      fuzzy = true;
    }
    // Does user want a fuzzy search
    if (filter[0] === '~') {
      fuzzy = true;
      filter =filter.slice(1);
    }


    let amountSearch = false;
    let dateSearch = false;
    let val1 = null;
    let val2 = null;

    // Does user want to restrict values to only income or expense?
    const negOnly = filter[0] === '-';
    const posOnly = filter[0] === '+';
    const startChar = (negOnly || posOnly) ? 1 : 0;
    let prefix = filter[startChar];

    // Does user want a less than/greater than/ or range search?
    if (prefix === '>' || prefix === '<' || prefix === '&') {
      const values = filter.slice(startChar + 1).split(',');
      if (!Number.isNaN(Number(values[0]))) {
        val1 = Number(values[0]);
        val2 = values.length > 1 ? Number(values[1]) : 0;
        amountSearch = !Number.isNaN(val1) && !Number.isNaN(val2);
      } else if (moment(values[0]).isValid()) {  // date?
        val1 = moment(values[0]);
        val2 = values.length > 1 ? moment(values[1]) : moment();
        dateSearch = val1.isValid() && val2.isValid();
      }
      // No PREFIX, so if text is a date or a number, apply a special search to them
    } else if (!Number.isNaN(Number(filter))) {
      amountSearch = true;
      const filterAsAmount = Number(filter);
      const variancePct = fuzzy ? .02 : 0;
      // construct a range search
      val2 = Number((Number(filterAsAmount) * (1 + variancePct)).toFixed(2));
      val1 = Number((Number(filterAsAmount) * (1 - variancePct)).toFixed(2));
      amountSearch = true;
      prefix = '&';
    } else if (moment(filter).isValid()) {
      dateSearch = true;
      const dateSpread = fuzzy ? 5 : 0;
      val1 = moment(filter).subtract(dateSpread, 'days');
      val2 = moment(filter).add(dateSpread, 'days');
      prefix = '&';
    }

    return txns.filter((txn) => {

      /*
       * AMOUNT SEARCH
       */
      if (amountSearch) {
        if ((negOnly && txn.amount >=0) || (posOnly && txn.amount <=0)) {
          return false;
        }
        const txnAmt = Math.abs(Number(txn.amount));
        switch (prefix) {
          case '>':
            return Math.abs(txnAmt) >= val1;
          case '<':
            return Math.abs(txnAmt)<= val1;
          case '&': {
            val1 = Math.abs(val1);
            val2 = Math.abs(val2);
            if (val1 > val2) {
              return (txnAmt >= val2) && (txnAmt <= val1);
            }
            return (txnAmt >= val1) && (txnAmt <= val2);
          }
          default:
            return Number(txn.amount) === Number(filter.slice(1));  // remove the math operand
        }
        /*
         * DATE SEARCH
         */
      } else if (dateSearch) {

        const txnDate = moment(txn.postedOn);
        switch (prefix) {
          case '>':
            return txnDate.isSameOrAfter(val1);
          case '<':
            return txnDate.isSameOrBefore(val1);
          case '&':
            return txnDate.isBetween(moment(val1).subtract(1,'day'), moment(val2).add(1, 'day'));
          default:
            return false;
        }

        /*
         * NON-AMOUNT SEARCH
         */
      } else if (filter === 'reviewed' || filter === 'notreviewed') {
        return txn.isReviewed ? filter === 'reviewed' : !(filter ==='reviewed');
      } else {
        let descString = `${txn.payee}~`;
        descString = `${descString}${moment(txn.postedOn).format('MM/DD/YYYY')}~`;
        if (txn.coa) {
          // TODO leverage longCats txlist preference to match search to display of category
          descString = `${descString}${getCoaString(txn.coa)}~`;
        }
        descString = `${descString}${txn.state}~`;
        if (txn.check && txn.check.number) {
          descString = `${descString}${txn.check.number}~`;
        }
        descString = `${descString}${String(numeral(txn.amount).format('$0,00.00'))}~`;

        if (txn.split && txn.split.items) {
          txn.split.items.forEach((item) => {
            // TODO leverage longCats txlist preference to match search to display of category
            descString = `${descString}${item.memo}~${String(item.amount)}~${makeTagsString(item.tags)}~${getCoaString(item.coa)}~`;
          });
        }
        if (txn.tags) {
          descString = `${descString}${makeTagsString(txn.tags)}~`;
        }
        if (txn.memo) {
          descString = `${descString}${txn.memo}~`;
        }
        // First, remove apostophes from the search
        filter = filter.replace("'", '');
        descString = descString.replace("'", '');
        filter = filter.replace("`", '');
        descString = descString.replace("`", '');
        // Convert special characters to spaces for the search
        filter = filter.replace(/([!"#%&'*()+,\-./:;<=>?\][@\\^_])/g, ' ');
        descString = descString.replace(/([!"#%&*'()+,`\-./:;<=>?\][@\\^_])/g, ' ');
        return descString.toLowerCase().indexOf(filter.toLowerCase()) >= 0;
      }
    });
  }
  return txns;
}
export function getFieldString(field, txn, longCats) {

  switch (field) {

    case 'postedOn':
    case 'date':
      return moment(txn.postedOn).format('MM/DD/YYYY');
    case 'category':
      return isSplitTxn(txn) ? 'SPLIT' : getCoaString(txn.coa, longCats);
    case 'account':
    case 'accountColor':
      return getAccountString(txn.accountId);
    case 'amount':
      return formatNumber(txn.amount, getCurrencyString(txn.accountId), '0,00.00');
    case 'balance':
      return formatNumber(txn.balance, getCurrencyString(txn.accountId), '0,00.00');
    case 'tags': {
      return makeTagsString(txn.tags) || '';
    }
    case 'check': {
      return txn.check && txn.check.number;
    }
    case 'status':
      return txn.state;
    case 'notes':
      return txn.memo ? txn.memo : '';
    default:
      return txn[field] ? txn[field] : '';
  }
}

export function makeTagsString(tags, useHashes) {
  let str = null;
  if (tags) {
    tags.forEach((x) => {
      const tag = getTagById(x.id) || x;
      if (tag && tag.name) {
        str = str ? `${str}${useHashes ? ' ' : '/'}` : '';
        str = `${str}${useHashes ? `#${tag.name}` : tag.name}`;
      }
    });
  }
  return str;
}

